import React from 'react';

import { CreateShippingPresetsBody } from './custom-card-bodies/create-shipping-presets-body';
import { ShippingWithConnectBody } from './custom-card-bodies/shipping-with-connect-body';
import { TagYourOrdersBody } from './custom-card-bodies/tag-your-orders-body';
import { AddTagButton } from './engagement-action-buttons/add-tag-button';
import { AddTaxIDButton } from './engagement-action-buttons/add-tax-id-button';
import { CreateCustomPackageButton } from './engagement-action-buttons/create-custom-package-button';
import { CreateDemoOrderButton } from './engagement-action-buttons/create-demo-order-button';
import { LabelLayoutButton } from './engagement-action-buttons/label-layout-button';
import { NewRuleButtonWithContext } from './engagement-action-buttons/new-automation-rule-button';
import { prepopulatedInsuranceRule, prepopulatedInsuranceFilter } from './engagement-action-buttons/prepopulated-insurance-rule';
import { prepopulatedWeightRule } from './engagement-action-buttons/prepopulated-weight-rule';
import barcodePresets from './images/barcode-presets.png';
import createShippingPresets from './images/create-shipping-presets.png';
import insightsReports from './images/insights-reports.png';
import inventorySync from './images/inventory-sync.png';
import orderSync from './images/order-sync.gif';
import shippingLabelLayout from './images/shipping-label-layout.png';
import tagYourOrders from './images/tag-your-orders.png';
import validateAddress from './images/validate-address.png';
import { messages } from './messages';
import { useHasDemoOrdersFeature } from '../../../../hooks/use-has-demo-orders-feature';
import { useIsTaxIdAvailable } from '../../../../hooks/use-is-tax-id-available';
import { useUserHasAdministrationRole } from '../../../../hooks/use-user-role';
import {
  getConnectSettingsPath,
  getInternationalSettingsPath,
  getInventorySyncSettingPath,
  getPackingslipsSettingsPath,
} from '../../../../routes/constants';
import { linkIDs } from '../../../../services/link-service/links';

export const TUTORIAL = 'tutorial';
export const ARTICLE = 'article';
export const ACTION = 'action';
export const NAVIGATION = 'navigation';
export const POP_OVER_VIDEO = 'popOverVideo';

const ONE_MIN = messages.oneMin;
const ONE_TO_TWO_MINS = messages.oneToTwoMins;
const TWO_MINS = messages.twoMins;
const ONE_TO_FIVE_MINS = messages.oneToFiveMins;
const TWO_TO_THREE_MINS = messages.twoToThreeMins;
const THREE_TO_FOUR_MINS = messages.threeToFourMins;
const THREE_TO_FIVE_MINS = messages.threeToFiveMins;
const FIVE_TO_SEVEN_MINS = messages.fiveToSevenMins;

export const ALL = 'All';
const SHIPPING = 'Shipping';
const ORDERS = 'Orders';
const POST_SHIP = 'Post Ship';
const AUTOMATIONS = 'Automations';
const BRANDING = 'Branding';
const INTERNATIONAL = 'International';
const INVENTORY = 'Inventory';
const REPORTING = 'Reporting';

export const filterButtons = [
  {
    tag: ALL,
    tagText: messages.viewAll,
  },
  {
    tag: SHIPPING,
    tagText: messages.shipping,
  },
  {
    tag: ORDERS,
    tagText: messages.orders,
  },
  {
    tag: POST_SHIP,
    tagText: messages.postShip,
  },
  {
    tag: AUTOMATIONS,
    tagText: messages.automations,
  },
  {
    tag: BRANDING,
    tagText: messages.branding,
  },
  {
    tag: INTERNATIONAL,
    tagText: messages.international,
  },
  {
    tag: INVENTORY,
    tagText: messages.inventory,
  },
  {
    tag: REPORTING,
    tagText: messages.reporting,
  },
];

/**
 * @typedef {object} EngagementCard
 * @property {number} id - The unique identifier for the card.
 * @property {object} title - The title object.
 * @property {?object} description - The description object.
 * @property {?Function} customBody - A function to customize the card body.
 * @property {?string} videoId - The ID of the associated video.
 * @property {string} kbUrl - The URL for the knowledge base article.
 * @property {object} length - The length description of the video or content.
 * @property {string} tag - The tag associated with the card.
 * @property {object} tagText - The text associated with the tag.
 * @property {string} cardType - The type of card.
 * @property {?object} buttonText - The text for the button.
 * @property {?Function} actionButton - A function that defines the action for the button.
 * @property {?string} image - The URL or base64 string of the PNG image.
 * @property {?string} navigationTarget - The URL for navigation.
 */

/**
 * @type {EngagementCard[]}
 */

// TODO remove custom body in favor of description and descriptionValues as arrays in SPD-18421

const alwaysShow = () => true;

export const engagementCardData = [
  {
    id: 0,
    title: messages.addTaxIDTitle,
    description: messages.addTaxIDBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.WhyIsTaxIDRequired,
    length: ONE_MIN,
    tag: SHIPPING,
    tagText: messages.shipping,
    cardType: ACTION,
    buttonText: messages.addTaxIDButton,
    actionButton: () => <AddTaxIDButton />,
    image: null,
    navigationTarget: null,
    getShouldRender: useIsTaxIdAvailable,
  },
  {
    id: 1,
    title: messages.selectLabelLayoutTitle,
    description: messages.selectLabelLayoutBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.SelectLabelLayout,
    length: ONE_TO_TWO_MINS,
    tag: SHIPPING,
    tagText: messages.shipping,
    cardType: ACTION,
    buttonText: messages.selectLayoutButton,
    actionButton: (onActionClicked) => <LabelLayoutButton onActionClicked={onActionClicked} />,
    image: shippingLabelLayout,
    navigationTarget: null,
    getShouldRender: useUserHasAdministrationRole,
  },
  {
    id: 2,
    title: messages.compareRatesTitle,
    description: messages.compareRatesBody,
    customBody: null,
    videoId: 'axtm3qvmt2',
    kbUrl: linkIDs.CompareRates,
    length: ONE_TO_TWO_MINS,
    tag: SHIPPING,
    tagText: messages.shipping,
    cardType: TUTORIAL,
    buttonText: messages.tutorial,
    actionButton: null,
    image: null,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 3,
    title: messages.storeLogoTitle,
    description: messages.storeLogoBody,
    customBody: null,
    videoId: '5cuum1fyhx',
    kbUrl: linkIDs.WhatIsBranding,
    length: TWO_TO_THREE_MINS,
    tag: BRANDING,
    tagText: messages.branding,
    cardType: TUTORIAL,
    buttonText: messages.tutorial,
    actionButton: null,
    image: null,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 4,
    title: messages.createCustomPackageTitle,
    description: messages.createCustomPackageBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.CreateCustomPackage,
    length: ONE_TO_TWO_MINS,
    tag: SHIPPING,
    tagText: messages.shipping,
    cardType: ACTION,
    buttonText: messages.addPackage,
    actionButton: (onActionClicked) => <CreateCustomPackageButton onActionClicked={onActionClicked} />,
    image: null,
    navigationTarget: null,
    getShouldRender: useUserHasAdministrationRole,
  },

  {
    id: 5,
    title: messages.createAShippingLabelTitle,
    description: messages.createAShippingLabelBody,
    customBody: null,
    videoId: 'l5su8ef55h',
    kbUrl: linkIDs.CreatePrintYourFirstLabel,
    length: TWO_TO_THREE_MINS,
    tag: SHIPPING,
    tagText: messages.shipping,
    cardType: TUTORIAL,
    buttonText: messages.tutorial,
    actionButton: null,
    image: null,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 6,
    title: messages.syncOrdersTitle,
    description: messages.syncOrdersBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.ImportOrdersFromAStore,
    length: ONE_MIN,
    tag: ORDERS,
    tagText: messages.orders,
    cardType: ACTION,
    buttonText: null,
    actionButton: null,
    image: orderSync,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 7,
    title: messages.validateRecipientAddressTitle,
    description: messages.validateRecipientAddressBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.AddressValidation,
    length: ONE_TO_TWO_MINS,
    tag: ORDERS,
    tagText: messages.orders,
    cardType: ARTICLE,
    buttonText: null,
    actionButton: null,
    image: validateAddress,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 8,
    title: messages.automationTutoriaTitle,
    description: messages.automationTutorialBody,
    customBody: null,
    videoId: 'ao8h6jiwyd',
    kbUrl: linkIDs.CreateAutomationRules,
    length: TWO_TO_THREE_MINS,
    tag: AUTOMATIONS,
    tagText: messages.automations,
    cardType: TUTORIAL,
    buttonText: messages.tutorial,
    actionButton: null,
    image: null,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 9,
    title: messages.automationRecommendationWeightTitle,
    description: messages.automationRecommendationWeightBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.AutomationRuleExamples,
    length: ONE_TO_TWO_MINS,
    tag: AUTOMATIONS,
    tagText: messages.automations,
    cardType: ACTION,
    buttonText: messages.createRule,
    actionButton: (onActionClicked) => (
      <NewRuleButtonWithContext onActionClicked={onActionClicked} preconfiguredRule={prepopulatedWeightRule} />
    ),
    image: null,
    navigationTarget: null,
    getShouldRender: useUserHasAdministrationRole,
  },
  {
    id: 10,
    title: messages.automationRecommendationInsuranceTitle,
    description: messages.automationRecommendationInsuranceBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.AddInsuranceToShipments,
    length: ONE_TO_TWO_MINS,
    tag: AUTOMATIONS,
    tagText: messages.automations,
    cardType: ACTION,
    buttonText: messages.createRule,
    actionButton: (onActionClicked) => (
      <NewRuleButtonWithContext
        onActionClicked={onActionClicked}
        preconfiguredFilter={prepopulatedInsuranceFilter}
        preconfiguredRule={prepopulatedInsuranceRule}
      />
    ),
    image: null,
    navigationTarget: null,
    getShouldRender: useUserHasAdministrationRole,
  },
  {
    id: 11,
    title: messages.tagYourOrdersTitle,
    description: null,
    customBody: <TagYourOrdersBody />,
    videoId: null,
    kbUrl: linkIDs.TagOrders,
    length: ONE_TO_TWO_MINS,
    tag: ORDERS,
    tagText: messages.orders,
    cardType: ACTION,
    buttonText: messages.addTag,
    actionButton: (onActionClicked) => <AddTagButton onActionClicked={onActionClicked} />,
    image: tagYourOrders,
    navigationTarget: null,
    getShouldRender: useUserHasAdministrationRole,
  },
  {
    id: 12,
    title: messages.customizeEmailTitle,
    description: messages.customizeEmailBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.CreateCustomEmailTemplates,
    length: FIVE_TO_SEVEN_MINS,
    tag: BRANDING,
    tagText: messages.branding,
    cardType: ARTICLE,
    buttonText: messages.goToTemplates,
    actionButton: null,
    image: null,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 13,
    title: messages.customizePackingSlipsTitle,
    description: messages.customizePackingSlipsBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.CustomizePackingSlips,
    length: ONE_TO_FIVE_MINS,
    tag: BRANDING,
    tagText: messages.branding,
    cardType: NAVIGATION,
    buttonText: messages.goToTemplates,
    actionButton: null,
    image: null,
    navigationTarget: getPackingslipsSettingsPath(),
    getShouldRender: useUserHasAdministrationRole,
  },
  {
    id: 14,
    title: messages.createShippingPresetsTitle,
    description: null,
    customBody: <CreateShippingPresetsBody />,
    videoId: null,
    kbUrl: linkIDs.CreateShippingPresets,
    length: ONE_TO_TWO_MINS,
    tag: SHIPPING,
    tagText: messages.shipping,
    cardType: ARTICLE,
    buttonText: null,
    actionButton: null,
    image: createShippingPresets,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 15,
    title: messages.barcodePresetsTitle,
    description: messages.barcodePresetsBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.BarcodePresets,
    length: ONE_TO_TWO_MINS,
    tag: SHIPPING,
    tagText: messages.shipping,
    cardType: ARTICLE,
    buttonText: null,
    actionButton: null,
    image: barcodePresets,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 16,
    title: messages.brandedTrackingTitle,
    description: messages.brandedTrackingBody,
    customBody: null,
    videoId: '7zpexgaxs2',
    kbUrl: linkIDs.BrandedTrackingPage,
    length: ONE_TO_TWO_MINS,
    tag: POST_SHIP,
    tagText: messages.postShip,
    cardType: TUTORIAL,
    buttonText: messages.tutorial,
    actionButton: null,
    image: null,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 17,
    title: messages.printingWithConnectTitle,
    description: null,
    customBody: <ShippingWithConnectBody />,
    videoId: null,
    kbUrl: linkIDs.PrintingWithConnect,
    length: THREE_TO_FIVE_MINS,
    tag: SHIPPING,
    tagText: messages.shipping,
    cardType: NAVIGATION,
    buttonText: messages.goToInstallation,
    actionButton: null,
    image: null,
    navigationTarget: getConnectSettingsPath(),
    getShouldRender: useUserHasAdministrationRole,
  },
  {
    id: 18,
    title: messages.insightsReportsTitle,
    description: messages.insightsReportsBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.InsightsReports,
    length: ONE_TO_TWO_MINS,
    tag: REPORTING,
    tagText: messages.reporting,
    cardType: ARTICLE,
    buttonText: null,
    actionButton: null,
    image: insightsReports,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 19,
    title: messages.shipInternationallyTitle,
    description: messages.shipInternationallyBody,
    customBody: null,
    videoId: '2n2lq2l7ov',
    kbUrl: linkIDs.ShipInternationally,
    length: THREE_TO_FOUR_MINS,
    tag: INTERNATIONAL,
    tagText: messages.international,
    cardType: TUTORIAL,
    buttonText: messages.tutorial,
    actionButton: null,
    image: null,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 20,
    title: messages.internationalSettingsTitle,
    description: messages.internationalSettingsBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.InternationalSettings,
    length: ONE_TO_TWO_MINS,
    tag: INTERNATIONAL,
    tagText: messages.international,
    cardType: NAVIGATION,
    buttonText: messages.goToSettings,
    actionButton: null,
    image: null,
    navigationTarget: getInternationalSettingsPath(),
    getShouldRender: useUserHasAdministrationRole,
  },
  {
    id: 21,
    title: messages.inventorySyncTitle,
    description: messages.inventorySyncBody,
    customBody: null,
    videoId: null,
    kbUrl: linkIDs.InventorySync,
    length: ONE_TO_TWO_MINS,
    tag: INVENTORY,
    tagText: messages.inventory,
    cardType: NAVIGATION,
    buttonText: messages.goToSettings,
    actionButton: null,
    image: inventorySync,
    navigationTarget: getInventorySyncSettingPath(),
    getShouldRender: useUserHasAdministrationRole,
  },
  {
    id: 23,
    title: messages.returnLabelTitle,
    description: messages.returnLabelBody,
    customBody: null,
    videoId: 'frr430ojq0',
    kbUrl: linkIDs.ReturnLabels,
    length: TWO_MINS,
    tag: POST_SHIP,
    tagText: messages.postShip,
    cardType: TUTORIAL,
    buttonText: messages.tutorial,
    actionButton: null,
    image: null,
    navigationTarget: null,
    getShouldRender: alwaysShow,
  },
  {
    id: 24,
    title: messages.demoOrderTitle,
    description: messages.demoOrderBody,
    customBody: null,
    videoId: null,
    kbUrl: null,
    length: TWO_MINS,
    tag: ORDERS,
    tagText: messages.orders,
    cardType: ACTION,
    buttonText: messages.demoOrderButton,
    actionButton: (onActionClicked, setIsLoading) => (
      <CreateDemoOrderButton onActionClicked={onActionClicked} setIsLoading={setIsLoading} />
    ),
    image: null,
    navigationTarget: null,
    getShouldRender: useHasDemoOrdersFeature,
  },
];
